import type { BillingChart, CompanyNameSuggestion, CompanyReportSection } from "@scoach/types";
import { Button, SegmentedControl, Spinner } from "@scoach/ui";
import { useEffect, useState } from "react";

import { connectBigQuery } from "../../lib/firebase.ts";
import { getBigQueryTokenSilent } from "../../lib/googleToken.ts";
import { useAuthStore } from "../auth/store.ts";
import { BillingCharts, normalizeBillingCharts, parseMarkdownTables } from "./BillingCharts.tsx";
import { CompanyPicker } from "./CompanyPicker.tsx";
import { Markdown } from "./Markdown.tsx";
import { companiesApi } from "./api.ts";

type Tab = "research" | "billing" | "growth";
type Section =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; markdown: string }
  | { status: "error"; message: string };

// Auth/scope failures where the stored BigQuery token itself is no good — clear it so
// the next attempt re-mints. NOTE: `session_expired` is deliberately NOT here — per the
// CRM contract it means "token fine, but the user lacks BigQuery IAM access", so
// re-consenting is futile and just causes a connect loop; we surface a message instead.
const TOKEN_REAUTH_CODES = new Set([
  "token_expired",
  "token_invalid",
  "missing_scope",
  "no_token",
  "token_missing",
]);

function friendly(code: string | undefined): string {
  switch (code) {
    case "no_token":
    case "token_missing":
      return "Connect BigQuery to load this section.";
    case "missing_scope":
      return "The BigQuery permission wasn't granted — try Connect again and approve both scopes.";
    case "token_expired":
      return "Your BigQuery session expired — click Connect again.";
    case "session_expired":
      return "Your Google account doesn't have BigQuery access for this billing data (IAM). Reconnecting won't help — request access to the billing dataset.";
    case "wrong_domain":
      return "Billing is only available to authorized corporate accounts.";
    case "no_billing_data":
      return "No billing data found for this company.";
    case "company_not_found":
    case "unknown_company":
      return "No matching company in the billing dictionary.";
    case "timeout":
      return "The report timed out. Try again in a moment.";
    default:
      return "This section is unavailable right now.";
  }
}

/** The sign-in BigQuery token from the store, or "" if absent/expired. */
function validStoredToken(): string {
  const { bqAccessToken, bqTokenExp } = useAuthStore.getState();
  return bqAccessToken && (!bqTokenExp || bqTokenExp > Date.now()) ? bqAccessToken : "";
}

function toSection(s: CompanyReportSection | undefined): Section {
  if (!s) return { status: "error", message: friendly(undefined) };
  if (s.status === "ok") {
    const md = s.markdown ?? s.report ?? "";
    return md ? { status: "ok", markdown: md } : { status: "error", message: "Nothing to show." };
  }
  return { status: "error", message: friendly(s.code) };
}

/**
 * Company intel content with Research / Billing / Growth tabs — no modal chrome, so it
 * can be embedded (e.g. in the quick-meeting popup's left pane) or wrapped in a Modal.
 * Research is Sally's own Gemini research (passed in via `researchMarkdown` to reuse an
 * already-fetched profile, or self-fetched when omitted). Billing + Growth come from the
 * CRM report — a 2-4 min call run as the user via their BigQuery token. It auto-fires the
 * moment a company name is known (in parallel with research) whenever we already hold a
 * BigQuery token from sign-in, so there's no button and no wait. Only when no token is
 * available (an older session) do we show a small Connect link — a Google popup needs a
 * click, so it can't be auto-triggered.
 */
export function CompanyReportPanel({
  companyName,
  researchMarkdown,
  researchLoading,
  expanded,
  onToggleExpand,
  searchable,
}: {
  companyName: string;
  /** When provided, used for the Research tab (reuse an already-fetched profile). When
   * undefined, the panel fetches research itself. */
  researchMarkdown?: string;
  researchLoading?: boolean;
  /** When embedded next to a setup form, whether the intel pane is expanded (taller,
   * wider). Renders an Expand/Collapse toggle only when `onToggleExpand` is given. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** Show a company search box and don't auto-load anything until the user picks a
   * company. Used on internal meetings, which have no fixed customer — the rep can
   * look up any company on demand instead of seeing preloaded intel. */
  searchable?: boolean;
}) {
  const [tab, setTab] = useState<Tab>("research");
  const [research, setResearch] = useState<Section>({ status: "idle" });
  const [billing, setBilling] = useState<Section>({ status: "idle" });
  const [billingCharts, setBillingCharts] = useState<BillingChart[]>([]);
  const [growth, setGrowth] = useState<Section>({ status: "idle" });
  // The company the panel is currently showing. Seeded from the prop; in searchable
  // mode the user changes it via the search box (nothing loads until they do).
  const [activeCompany, setActiveCompany] = useState(companyName);
  const [searchInput, setSearchInput] = useState(companyName);
  const [suggestions, setSuggestions] = useState<CompanyNameSuggestion[]>([]);

  // Keep the active company in sync when the prop changes (non-searchable callers).
  useEffect(() => {
    setActiveCompany(companyName);
    setSearchInput(companyName);
  }, [companyName]);

  // Load autocomplete suggestions once, only in searchable mode.
  useEffect(() => {
    if (!searchable) return;
    let cancelled = false;
    companiesApi
      .list()
      .then((items) => {
        if (!cancelled) setSuggestions(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [searchable]);

  // Research: reuse the provided markdown, or self-fetch when none was passed.
  useEffect(() => {
    setTab("research");
    if (!activeCompany) {
      setResearch({ status: "idle" });
      return;
    }
    if (researchMarkdown !== undefined) {
      setResearch(
        researchMarkdown
          ? { status: "ok", markdown: researchMarkdown }
          : researchLoading
            ? { status: "loading" }
            : { status: "error", message: "No research available for this company." },
      );
      return;
    }
    setResearch({ status: "loading" });
    let cancelled = false;
    companiesApi
      .research({ name: activeCompany })
      .then((p) => {
        if (cancelled) return;
        setResearch(
          p.markdown
            ? { status: "ok", markdown: p.markdown }
            : { status: "error", message: "No research available for this company." },
        );
      })
      .catch((e) => {
        if (!cancelled) setResearch({ status: "error", message: (e as Error).message });
      });
    return () => {
      cancelled = true;
    };
  }, [activeCompany, researchMarkdown, researchLoading]);

  // Run the CRM report with a known-good token (no popup). `isStale` lets a newer
  // company selection cancel this slow (2-4 min) call before it overwrites state.
  async function runReports(token: string, isStale: () => boolean) {
    setBilling({ status: "loading" });
    setGrowth({ status: "loading" });
    try {
      const r = await companiesApi.report(activeCompany, token);
      if (isStale()) return;
      // If the server rejected the token (expired/invalid/missing scope), drop it so
      // the next attempt re-consents instead of replaying a dead token.
      const codes = [r.billing, r.growth].map((s) => (s?.status === "error" ? s.code : undefined));
      if (codes.some((c) => c && TOKEN_REAUTH_CODES.has(c))) {
        useAuthStore.getState().setBqToken(null);
      }
      setBilling(toSection(r.billing));
      // Charts may arrive under `charts`, or (if the CRM shape shifts) elsewhere in the
      // billing section — try `charts` first, then the whole section, then the markdown.
      const b = (r.billing ?? {}) as unknown as Record<string, unknown>;
      let structured = normalizeBillingCharts(b.charts);
      if (structured.length === 0)
        structured = normalizeBillingCharts(b.chartData ?? b.graphs ?? b);
      const billingMd =
        (typeof b.markdown === "string" && b.markdown) ||
        (typeof b.report === "string" && b.report) ||
        (typeof b.content === "string" && b.content) ||
        (typeof b.text === "string" && b.text) ||
        "";
      const parsed = structured.length > 0 ? structured : parseMarkdownTables(billingMd);
      if (parsed.length === 0 && b.status === "ok") {
        // Neither structured charts nor markdown tables were usable — log the shape so
        // we can adapt to a CRM payload change (visible in the browser console).
        console.warn("[billing] no charts parsed; billing keys:", Object.keys(b), b.charts);
      }
      setBillingCharts(parsed);
      setGrowth(toSection(r.growth));
    } catch (e) {
      if (isStale()) return;
      const msg = (e as Error).message || "Failed to load reports";
      setBilling({ status: "error", message: msg });
      setGrowth({ status: "error", message: msg });
    }
  }

  // Auto-load Billing + Growth as soon as a company is known — in parallel with the
  // research fetch above — reusing the BigQuery token captured at sign-in. No button,
  // no second Google popup. When no token is held yet (an older session), stay idle so
  // the tab can offer a Connect link (a popup can't be auto-triggered without a click).
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on activeCompany; runReports reads it via closure
  useEffect(() => {
    setBilling({ status: "idle" });
    setBillingCharts([]);
    setGrowth({ status: "idle" });
    if (!activeCompany) return;
    let stale = false;
    void (async () => {
      // 1) A token we already hold. 2) A SILENT GIS token (no popup) — works once the
      // user has granted BigQuery access before, which is what makes this "connect once".
      // 3) Otherwise stay idle so the tab can offer a one-time Connect button.
      let token = validStoredToken();
      if (!token) {
        token = await getBigQueryTokenSilent();
        if (token) useAuthStore.getState().setBqToken(token, Date.now() + 55 * 60 * 1000);
      }
      if (!token || stale) return;
      await runReports(token, () => stale);
    })();
    return () => {
      stale = true;
    };
  }, [activeCompany]);

  // One-time Connect click: get a BigQuery token, persist it, then load.
  //
  // The popup MUST be opened synchronously within this click's user-activation
  // window. Previously we `await`ed a GIS token first and only fell back to the
  // Firebase popup on failure — but on origins the GIS OAuth client doesn't list
  // (e.g. QC), GIS rejects and that fallback popup lands OUTSIDE the gesture, so
  // the browser blocks it (the reported `auth/popup-blocked`). So: open the
  // Firebase popup first, with no await before it. GIS silent refresh still runs
  // in the auto-load effect for origins that are configured for it.
  async function connectAndLoad() {
    setBilling({ status: "loading" });
    setGrowth({ status: "loading" });
    try {
      const token = await connectBigQuery();
      useAuthStore.getState().setBqToken(token, Date.now() + 55 * 60 * 1000);
      // Best-effort: also mint a silent GIS token so later refreshes need no popup
      // (only works once the app origin is an Authorized JS origin on the client).
      void getBigQueryTokenSilent().then((t) => {
        if (t) useAuthStore.getState().setBqToken(t, Date.now() + 55 * 60 * 1000);
      });
      await runReports(token, () => false);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      const msg =
        err?.code === "auth/popup-blocked"
          ? "Your browser blocked the Google sign-in pop-up. Allow pop-ups for this site, then click Connect again."
          : err?.message || "Failed to load reports";
      setBilling({ status: "error", message: msg });
      setGrowth({ status: "error", message: msg });
    }
  }

  const active = tab === "research" ? research : tab === "billing" ? billing : growth;
  const isReport = tab === "billing" || tab === "growth";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {searchable && (
        <CompanyPicker
          value={searchInput}
          onChange={setSearchInput}
          onSelect={(pick) => {
            setSearchInput(pick.name);
            setActiveCompany(pick.name);
          }}
          priorSuggestions={suggestions}
          placeholder="Search a company…"
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SegmentedControl<Tab>
            size="sm"
            ariaLabel="Report section"
            value={tab}
            onChange={setTab}
            options={[
              { value: "research", label: "Research" },
              { value: "billing", label: "Billing" },
              { value: "growth", label: "Growth" },
            ]}
          />
        </div>
        {onToggleExpand && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleExpand}
            title={expanded ? "Shrink results" : "Expand results for a bigger view"}
          >
            {expanded ? "⤡ Collapse" : "⤢ Expand"}
          </Button>
        )}
      </div>
      {/* Fixed, stable height with its own scroll — do NOT stretch to the sibling pane
          (that caused a scrollbar/height feedback loop that flickered the popup).
          Grows when expanded so the charts get more room. */}
      <div
        style={{
          height: expanded ? "78vh" : "58vh",
          overflow: "auto",
          border: "1px solid var(--border, #e0e0e0)",
          borderRadius: 8,
          padding: "12px 14px",
          background: "var(--surface, #fff)",
        }}
      >
        {searchable && !activeCompany ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            Search a company above to see its research, billing, and growth.
          </div>
        ) : active.status === "loading" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-3)" }}>
            <Spinner size={16} />{" "}
            {isReport ? "Running the report (this can take 2-4 minutes)…" : "Researching…"}
          </div>
        ) : active.status === "ok" ? (
          tab === "billing" && billingCharts.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <BillingCharts charts={billingCharts} />
              <div style={{ marginTop: 8 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--text-3)",
                    marginBottom: 8,
                  }}
                >
                  Details
                </div>
                <Markdown>{active.markdown}</Markdown>
              </div>
            </div>
          ) : (
            <Markdown>{active.markdown}</Markdown>
          )
        ) : active.status === "error" ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>{active.message}</div>
        ) : isReport && !activeCompany ? (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            Pick a company to load its billing &amp; growth.
          </div>
        ) : isReport ? (
          // No stored BigQuery token (an older session) — a Google popup needs a click,
          // so offer one. After connecting once it's remembered and this won't reappear.
          <div
            style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-start" }}
          >
            <div style={{ fontSize: 13, color: "var(--text-3)" }}>
              Billing &amp; growth run a BigQuery query under your own Google account. Connect once
              to load them — we'll remember it next time.
            </div>
            <Button onClick={() => void connectAndLoad()}>Connect Google to load</Button>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>Loading…</div>
        )}
      </div>
    </div>
  );
}
